import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Storefront,
  MagnifyingGlassPlus,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { AppHeader } from "@/components/AppHeader";
import { FilterBar } from "@/components/FilterBar";
import { ColorChips } from "@/components/ColorChips";
import { Thumb, photoSrc, allPhotos } from "@/components/Thumb";
import { WebsiteFlags } from "@/components/WebsiteFlags";
import { PortalLightbox } from "@/components/PortalLightbox";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";

const TYPES = ["All", "Full Slabs", "Remnants"];

export default function Showroom() {
  const navigate = useNavigate();
  const [slabs, setSlabs] = useState([]);
  const featuredCount = slabs.filter((s) => s.showroom_featured).length;
  const [remnants, setRemnants] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState([]);
  const [open, setOpen] = useState(null);
  const [share, setShare] = useState(null);
  const [seconds, setSeconds] = useState(12);
  const publicUrl = `${window.location.origin}/inventory`;
  const displayUrl = `${window.location.origin}/display`;

  useEffect(() => {
    api
      .get("/showroom/share")
      .then(({ data }) => {
        setShare(data);
        setSeconds(data.display_seconds || 12);
      })
      .catch(() => setShare(null));
  }, []);

  const saveShare = async (next) => {
    try {
      const { data } = await api.put("/showroom/share", { ...share, ...next });
      setShare(data);
      setSeconds(data.display_seconds || 12);
      if (next.display_seconds !== undefined) {
        toast.success(`Screen changes every ${data.display_seconds}s`);
        return;
      }
      toast.success(
        data.public ? "Public showroom is live" : "Public showroom turned off",
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
      );
    }
  };

  const load = useCallback(async () => {
    const [s, r] = await Promise.all([
      api.get("/slabs", { params: { status: "In Stock" } }),
      api.get("/remnants", { params: { status: "Available" } }),
    ]);
    setSlabs(s.data);
    setRemnants(r.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const items = useMemo(() => {
    const mapped = [
      ...slabs.map((s) => ({
        key: `slab-${s.id}`,
        id: s.id,
        href: `/inventory/slabs/${s.id}`,
        kind: "Full Slab",
        status: s.status,
        pieces: s.pieces,
        slab_color: s.slab_color,
        color: s.slab_color,
        primary_color: s.primary_color,
        accent_color: s.accent_color,
        material: s.material,
        finish: s.finish,
        thickness: s.thickness,
        size: `${s.length_in}" x ${s.width_in}"`,
        sqft: Math.round(((s.length_in * s.width_in) / 144) * 10) / 10,
        qty: s.quantity,
        photos: s.photos,
        new_arrival: s.new_arrival,
        on_sale: s.on_sale,
      })),
      ...remnants.map((r) => ({
        key: `rem-${r.id}`,
        href: `/inventory/remnants/${r.id}`,
        kind: "Remnant",
        color: r.slab_color,
        material: "",
        finish: "",
        thickness: r.thickness,
        size: `${r.length_in}" x ${r.width_in}"`,
        sqft: r.usable_sqft,
        qty: 1,
        photos: r.photos,
      })),
    ];
    const needle = q.trim().toLowerCase();
    return mapped
      .filter((i) =>
        type.length === 0
          ? true
          : type.some((t) =>
              t === "Full Slabs"
                ? i.kind === "Full Slab"
                : t === "Remnants"
                  ? i.kind === "Remnant"
                  : true,
            ),
      )
      .filter((i) =>
        needle
          ? `${i.color} ${i.material} ${i.finish}`
              .toLowerCase()
              .includes(needle)
          : true,
      )
      .sort((a, b) => a.color.localeCompare(b.color));
  }, [slabs, remnants, q, type]);

  const gallery = useMemo(
    () =>
      items.flatMap((i) =>
        allPhotos(i).map((p, n) => ({
          photo: p,
          href: i.href,
          title: i.color,
          sub: `${i.kind} · ${i.size} · ${i.sqft} ft²${
            allPhotos(i).length > 1 ? ` · photo ${n + 1}` : ""
          }`,
        })),
      ),
    [items],
  );

  const openGallery = (item) => {
    const first = allPhotos(item)[0];
    if (!first) return;
    setOpen(
      Math.max(
        0,
        gallery.findIndex((g) => g.photo.id === first.id),
      ),
    );
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Showroom" />
      <main className="relative z-10 pb-16 pt-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-600">
              Brioni Granite
            </div>
            <h1 className="text-4xl font-black uppercase sm:text-5xl">
              What's In Stock
            </h1>
            <p
              className="mt-2 text-sm text-zinc-500"
              data-testid="showroom-count"
            >
              {items.length} pieces available today
            </p>
          </div>
        </div>

        {share && (
          <div
            className="mt-6 grid gap-3 lg:grid-cols-2"
            data-testid="showroom-panels"
          >
            <div
              className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              data-testid="showroom-share-panel"
            >
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Public showroom link
              </div>
              <div className="font-mono-data mt-1 break-all text-xs text-indigo-300">
                {share.public
                  ? publicUrl
                  : "Turn on to share this list with customers"}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-3">
              <span className="text-[11px] uppercase tracking-widest text-zinc-400">
                {share.public ? "Live" : "Off"}
              </span>
              <Switch
                data-testid="showroom-share-toggle"
                checked={share.public}
                onCheckedChange={(v) => saveShare({ public: v })}
              />
              {share.public && (
                <>
                  <Button
                    data-testid="showroom-copy-link"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(publicUrl);
                      toast.success("Link copied");
                    }}
                    className="h-10 border-zinc-700 bg-transparent px-3 text-[11px] uppercase tracking-widest hover:bg-zinc-800"
                  >
                    Copy link
                  </Button>
                  <a
                    href={publicUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="showroom-open-public"
                    className="flex h-10 items-center gap-1.5 rounded-md border border-zinc-700 px-3 text-[11px] uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
                  >
                    <ArrowSquareOut size={14} weight="bold" /> Open
                  </a>
                </>
              )}
            </div>
            </div>

            <div
              className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              data-testid="showroom-screen-panel"
            >
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Showroom screen link
                </div>
                <div className="font-mono-data mt-1 break-all text-xs text-indigo-300">
                  {displayUrl}
                </div>
                <div
                  className="mt-1 text-[11px] text-zinc-500"
                  data-testid="showroom-screen-count"
                >
                  {featuredCount} slab{featuredCount === 1 ? "" : "s"} on the
                  screen — tick <span className="text-zinc-300">Screen</span> on
                  a slab below to add it
                </div>
              </div>
              <div className="ml-auto flex items-center gap-3">
                <label className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-400">
                  Seconds
                  <input
                    type="number"
                    min="3"
                    max="120"
                    data-testid="showroom-display-seconds"
                    value={seconds}
                    onChange={(e) => setSeconds(e.target.value)}
                    onBlur={() => {
                      const n = Math.min(120, Math.max(3, Number(seconds) || 12));
                      setSeconds(n);
                      if (n !== share.display_seconds)
                        saveShare({ display_seconds: n });
                    }}
                    className="h-10 w-16 rounded-lg border border-zinc-800 bg-zinc-950 px-2 text-center text-sm text-zinc-200 outline-none focus:border-indigo-500"
                  />
                </label>
                <Button
                  data-testid="showroom-copy-display-link"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(displayUrl);
                    toast.success("Screen link copied");
                  }}
                  className="h-10 border-zinc-700 bg-transparent px-3 text-[11px] uppercase tracking-widest hover:bg-zinc-800"
                >
                  Copy link
                </Button>
                <a
                  href={displayUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="showroom-open-display"
                  className="flex h-10 items-center gap-1.5 rounded-md border border-zinc-700 px-3 text-[11px] uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
                >
                  <ArrowSquareOut size={14} weight="bold" /> Open
                </a>
              </div>
            </div>
          </div>
        )}

        <FilterBar
          className="mt-6"
          testidPrefix="showroom"
          search={{
            value: q,
            onChange: setQ,
            placeholder: "Search a color or material…",
            testid: "showroom-search",
          }}
          sections={[
            {
              key: "type",
              label: "Show",
              multi: true,
              values: type,
              allValue: "All",
              onChange: setType,
              options: TYPES.map((t) => [
                t,
                t,
                `showroom-type-${t.toLowerCase().replace(/ /g, "-")}`,
              ]),
            },
          ]}
        />

        {items.length === 0 ? (
          <div className="mt-10 rounded-lg border border-dashed border-zinc-800 p-16 text-center">
            <Storefront
              size={30}
              weight="bold"
              className="mx-auto text-zinc-700"
            />
            <p className="mt-4 text-sm text-zinc-400">
              Nothing matches — try another color.
            </p>
          </div>
        ) : (
          <div
            className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            data-testid="showroom-grid"
          >
            {items.map((i) => (
              <div
                key={i.key}
                data-testid={`showroom-tile-${i.key}`}
                className="group flex flex-col overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 text-left transition-colors duration-150 hover:border-indigo-500"
              >
                <div className="relative">
                  <button
                    type="button"
                    data-testid={`showroom-photo-${i.key}`}
                    onClick={() =>
                      allPhotos(i).length ? openGallery(i) : navigate(i.href)
                    }
                    className="block w-full cursor-zoom-in"
                  >
                    <Thumb
                      item={i}
                      testId={`showroom-thumb-${i.key}`}
                      className="h-52"
                    />
                  </button>
                  <span className="absolute left-2 top-2 flex flex-wrap gap-1.5">
                    {i.new_arrival && (
                      <span
                        data-testid={`showroom-badge-new-${i.key}`}
                        className="rounded-full border border-emerald-400/40 bg-emerald-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-300 backdrop-blur"
                      >
                        New arrival
                      </span>
                    )}
                    {i.on_sale && (
                      <span
                        data-testid={`showroom-badge-sale-${i.key}`}
                        className="rounded-full border border-amber-400/40 bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-300 backdrop-blur"
                      >
                        On sale
                      </span>
                    )}
                  </span>
                  {allPhotos(i).length > 0 && (
                    <span className="font-mono-data absolute bottom-2 right-2 flex items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-950/80 px-2 py-1 text-[10px] uppercase tracking-widest text-zinc-300 backdrop-blur">
                      <MagnifyingGlassPlus size={11} weight="bold" />
                      {allPhotos(i).length > 1
                        ? `${allPhotos(i).length} photos`
                        : "Zoom"}
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="truncate text-lg font-bold">{i.color}</h3>
                    <span className="shrink-0 rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] uppercase tracking-widest text-zinc-400">
                      {i.kind}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-zinc-400">
                    {[i.material, i.finish, i.thickness]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  <ColorChips
                    primary={i.primary_color}
                    accent={i.accent_color}
                    testId={`showroom-colors-${i.key}`}
                    className="mt-2"
                  />
                  <p className="font-mono-data mt-2 text-sm text-yellow-400">
                    {i.size} · {i.sqft} ft²
                    {i.qty > 1 ? ` · ${i.qty} available` : ""}
                  </p>
                  {i.kind === "Full Slab" && (
                    <WebsiteFlags
                      slab={i}
                      prefix={`showroom-${i.key}`}
                      className="mt-3"
                      onSaved={(next) =>
                        setSlabs((list) =>
                          list.map((s) => (s.id === next.id ? next : s)),
                        )
                      }
                    />
                  )}
                  <div className="mt-4 flex gap-2">
                    <button
                      type="button"
                      data-testid={`showroom-gallery-btn-${i.key}`}
                      disabled={!allPhotos(i).length}
                      onClick={() => openGallery(i)}
                      className="flex h-11 flex-1 items-center justify-center gap-2 rounded-md bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400 disabled:opacity-30"
                    >
                      <MagnifyingGlassPlus size={14} weight="bold" /> Gallery
                    </button>
                    <button
                      type="button"
                      data-testid={`showroom-details-btn-${i.key}`}
                      onClick={() => navigate(i.href)}
                      className="flex h-11 flex-1 items-center justify-center gap-2 rounded-md border border-zinc-700 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:border-indigo-400 hover:text-white"
                    >
                      <ArrowSquareOut size={14} weight="bold" /> Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {open !== null && gallery.length > 0 && (
        <PortalLightbox
          prefix="showroom-lightbox"
          shots={gallery}
          index={open}
          srcOf={(_, shot) => photoSrc(shot.photo)}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}
